import { BoardChat } from '../components/chat/BoardChat';
import React from 'react';
import {Box, Button, Grid, Link, Skeleton, Stack, Typography} from '@mui/material';
import {Link as RouterLink, useLocation, useNavigate, useParams} from 'react-router-dom';
import TvIcon from '@mui/icons-material/Tv';
import CssBaseline from "@mui/material/CssBaseline"; // Import the useNavigate hook
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import {Alert} from '@mui/material';
import {useBoard, useRenameBoard} from "../data/board";
import {canEditBoard, canViewBoardTab} from "../types/task";
import EditableText from "../components/tasks/EditableText";
import DropdownButton from "../components/tasks/DropDownButton";
import Board from "../components/tasks/Board";
import {BoardManager} from "../components/tasks/BoardManager";
import {WeeklyTasksView} from "../components/tasks/WeekTaskView";
import {KioskDayboard} from "../components/tasks/KioskDayboard";
import {ArchivedTasksView} from "../components/tasks/ArchivedTasksView";
import {PageHeader} from "../components/layouts/PageHeader";
import {StatsView} from "../components/tasks/StatsView";
import {queryKeys} from '../data/queryKeys';
import {KIOSK_VIEW, wantsKioskView} from '../utilities/kioskView';


interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

// Defined at module level on purpose: declared inside BoardPage it would be a new component
// on every render, remounting the tab contents (and resetting their state) whenever the
// board data refreshes.
function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 2.5 }}>{children}</Box>}
        </div>
    );
}

const BoardPage: React.FC = () => {
    const { boardId } = useParams<{ boardId: string }>();
    const navigate = useNavigate();
    const { search } = useLocation();
    // Anyone with access can look at the board as a kiosk display shows it (?view=kiosk) and come back; for an account that is
    // restricted to the board, that is all there is, and there is nowhere to come back to.
    const previewingKiosk = wantsKioskView(search);

    const {data: board, isLoading} = useBoard(boardId);
    const {mutate: updateBoardTitle} = useRenameBoard(boardId);

    const [value, setValue] = React.useState(0);
    const handleChangee = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    function a11yProps(index: number) {
        return {
            id: `simple-tab-${index}`,
            'aria-controls': `simple-tabpanel-${index}`,
        };
    }

    return (

        <Box sx={{ display: 'flex', flexGrow: 1 }}>
            <CssBaseline />

            {/* Fill the row. Left to size itself it follows its content's natural width, so the whole page
                would change width with, say, the shape of the photo currently showing in the kiosk carousel. */}
            <Grid container sx={{ flexGrow: 1, minWidth: 0 }}>
                {isLoading && <Skeleton variant="rounded" height={360} width="100%"/>}
                {!isLoading && !board && (
                    <Alert severity="error">You don't have access to this board.</Alert>
                )}
                {!isLoading && board && (board.isKiosk || previewingKiosk) && (
                    <Box sx={{ width: '100%', minWidth: 0 }}>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2.5}}>
                            <Typography component="h1" sx={{fontSize: {xs: 20, sm: 23}, fontWeight: 600, letterSpacing: '-0.025em'}}>{board.title}</Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                {!board.isKiosk && <Button size="small" variant="outlined" onClick={() => navigate(`/boards/${boardId}`)}>Exit kiosk view</Button>}
                            </Stack>
                        </Box>
                        <KioskDayboard
                            boardId={boardId}
                            viewerRole={board.viewerRole}
                            photoUrls={board.kioskPhotoUrls}
                            message={board.kioskMessage}
                        />
                    </Box>
                )}
                {!isLoading && board && !board.isKiosk && !previewingKiosk && (
                    // Allowed to shrink below its content, so wide content (the board's columns) scrolls inside itself.
                    <Box sx={{ width: '100%', minWidth: 0 }}>
                        <Link component={RouterLink} to="/boards" underline="hover" sx={{display: 'inline-block', fontSize: 12, color: 'text.secondary', mb: 1.5}}>All boards</Link>
                        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
                            <EditableText
                                value={board.title}
                                variant="h4"
                                component="h1"
                                sx={{fontSize: {xs: 25, sm: 30}, fontWeight: 650, letterSpacing: "-0.035em"}}
                                onChange={(newTitle: string) => updateBoardTitle(newTitle)}
                                disabled={!canEditBoard(board.viewerRole)}
                            />
                            {board.viewerRole === "owner" && <DropdownButton boardId={boardId} />}
                            <Button size="small" variant="outlined" startIcon={<TvIcon />} onClick={() => navigate(`/boards/${boardId}?${KIOSK_VIEW}`)} sx={{ ml: 'auto' }}>
                                Kiosk view
                            </Button>
                        </Box>

                        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, bgcolor: 'background.paper', px: {xs: 0.5, sm: 1}, flexGrow: 1 }}>
                            <Tabs value={value} onChange={handleChangee} aria-label="Board views" variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
                                <Tab label="Week View" value={0} {...a11yProps(0)} />
                                <Tab label="Board View" value={1} {...a11yProps(1)} />
                                <Tab label="Chat" value={5} {...a11yProps(5)} />
                                {canViewBoardTab(board.viewerRole, "stats", board.isKiosk) && (
                                    <Tab label="Stats" sx={{ml: {sm: "auto"}}} value={4} {...a11yProps(4)} />
                                )}
                                {canViewBoardTab(board.viewerRole, "manage", board.isKiosk) && (
                                    <Tab label="Manage Board" value={2} {...a11yProps(2)} />
                                )}
                                {canViewBoardTab(board.viewerRole, "archive", board.isKiosk) && (
                                    <Tab label="Archive" value={3} {...a11yProps(3)} />
                                )}

                            </Tabs>
                        </Box>
                        <CustomTabPanel value={value} index={0}>
                            <WeeklyTasksView
                                boardId={boardId}
                                viewerRole={board.viewerRole}
                                sections={board.sectionOrder.map((id) => ({id, title: board.sectionsById?.[id]?.title ?? 'Untitled section'}))}
                            />
                        </CustomTabPanel>
                        <CustomTabPanel value={value} index={5}>
                            <BoardChat key={boardId} boardId={boardId!} />
                        </CustomTabPanel>
                        <CustomTabPanel value={value} index={1}>
                            <Board />
                        </CustomTabPanel>

                        {canViewBoardTab(board.viewerRole, "stats", board.isKiosk) && (
                            <CustomTabPanel value={value} index={4}>
                                <StatsView
                                    boardId={boardId}
                                    sections={board.sectionOrder.map((id) => ({id, title: board.sectionsById?.[id]?.title ?? 'Untitled section'}))}
                                />
                            </CustomTabPanel>
                        )}

                        {canViewBoardTab(board.viewerRole, "manage", board.isKiosk) && (
                            <CustomTabPanel value={value} index={2}>
                                <BoardManager boardId={boardId} canManageMembers={board.viewerRole === "owner"} canEdit={canEditBoard(board.viewerRole)} sections={board.sectionOrder.map((id) => ({id, title: board.sectionsById?.[id]?.title ?? 'Untitled section'}))} kioskMessage={board.kioskMessage} kioskPhotoUrls={board.kioskPhotoUrls} archiveAfterDays={board.archiveAfterDays} />
                            </CustomTabPanel>
                        )}

                        {canViewBoardTab(board.viewerRole, "archive", board.isKiosk) && (
                            <CustomTabPanel value={value} index={3}>
                                <ArchivedTasksView
                                    boardId={boardId}
                                    archiveAfterDays={board.archiveAfterDays}
                                    sections={board.sectionOrder.map((id) => ({id, title: board.sectionsById?.[id]?.title ?? 'Untitled section'}))}
                                />
                            </CustomTabPanel>
                        )}
                    </Box>
                )}
            </Grid>
        </Box>
    );
};

export default BoardPage;
